/**
 * How loud the music is at a given moment.
 *
 * A dynamic mark is a step -- the music is *mf* from here -- and a hairpin is a slope
 * between two steps. Reading them note by note as the bar is traversed works for the steps
 * and cannot work for the slopes: a crescendo has to know what it is growing towards, and
 * what it is growing towards is written after it. So the marks are collected first, as a
 * curve over the whole played timeline, and every note then asks the curve what it is worth
 * where it stands.
 *
 * The curve is in ticks of the played order, not seconds, so that a crescendo through a
 * tempo change slopes evenly over the *notes* it covers rather than over the clock, and so
 * that the answer does not change when the piece is played faster.
 *
 * A hairpin with nothing written at the end of it is the ordinary case -- most are -- and
 * it grows or falls by roughly one mark's worth, which is what a player does with it. A
 * hairpin that does end at a mark reaches exactly that mark, which is the other ordinary
 * case and the one where being wrong would be audible. Either way the music stays where the
 * hairpin took it until the next mark: a crescendo does not fall back when its line ends. A
 * mark written under a hairpin is a point it passes through: it slopes to the mark and on from
 * it, and one with nothing at its end grows from that mark, not from where it began.
 *
 * Five marks are not steps at all. *sf*, *sfz*, *rf* and *rfz* stress the notes struck where
 * they stand and leave the music as loud as it was; *fp* strikes them forte and drops at once
 * to piano, which is then the level from there on. Read as steps, one *fp* would leave the
 * rest of the passage forte.
 *
 * A note is not one loudness either. Where a hairpin runs through a held note, the note
 * swells or fades with it, and an *fp* note falls to piano after it is struck; the points it
 * moves through are its {@link Swell}.
 */

import { PPQ } from "../document/duration";
import type { DynamicMark } from "../document/types";
import type { Swell } from "./played";

/** How loud each mark is, from 0 to 1. */
export const VELOCITIES: Readonly<Record<DynamicMark, number>> = {
  pppp: 0.08,
  ppp: 0.14,
  pp: 0.22,
  p: 0.33,
  mp: 0.45,
  mf: 0.58,
  f: 0.72,
  ff: 0.85,
  fff: 0.94,
  ffff: 1,
  sf: 0.9,
  sfz: 0.95,
  fp: 0.8,
  rf: 0.8,
  rfz: 0.85,
};

/** The marks that belong to the notes struck where they stand, not to what follows. */
const MOMENTARY: ReadonlySet<DynamicMark> = new Set(["sf", "sfz", "rf", "rfz", "fp"]);

/**
 * How long an *fp* note takes to fall to piano: a semiquaver, the shortest note an ornament is
 * given in `realize.ts`, and never more than half the note, so a short note is still heard
 * struck forte.
 */
const SETTLE = PPQ / 4;

/**
 * How far apart a held note's loudness is read along a hairpin: a beat. The loudness is
 * sounded as straight lines between the points, so reading it every beat keeps it on the
 * curve rather than cutting across it.
 */
const STRIDE = PPQ;

/** How far past a hairpin a mark may stand and still be what the hairpin grows towards. */
const REACH = PPQ * 4;

/** How much louder a crescendo with nothing written at the end of it ends up. */
const GROWTH = 1.45;

/** How much of itself a diminuendo with nothing written at the end of it keeps. */
const DECAY = 0.62;

/** The softest and loudest anything is allowed to be sounded. */
const FLOOR = 0.03;
const CEILING = 1;

/** A mark reached: from here on, this loud. */
export interface Level {
  readonly ticks: number;
  readonly velocity: number;
}

/** A hairpin: a slope from where it opens to where it closes. */
export interface Ramp {
  readonly from: number;
  readonly to: number;
  readonly rising: boolean;
}

/** A mark that stresses the notes struck at one moment: *sf*, *sfz*, *rf*, *rfz*, *fp*. */
export interface Strike {
  readonly ticks: number;
  readonly velocity: number;
  /** Whether the note falls back to the level in force after it is struck, as *fp* does. */
  readonly settles: boolean;
}

/** Everything one part says about how loud it is, over the whole played order. */
export interface Curve {
  /** In ticks order. */
  readonly levels: readonly Level[];
  readonly ramps: readonly Ramp[];
  readonly strikes: ReadonlyMap<number, Strike>;
  /** How loud the part is before it has been told. */
  readonly base: number;
}

/** What a dynamic mark reached at a moment says: a level from here on, a stress here, or both. */
export function reading(
  mark: DynamicMark,
  ticks: number,
): { readonly level: Level | null; readonly strike: Strike | null } {
  if (!MOMENTARY.has(mark)) return { level: { ticks, velocity: VELOCITIES[mark] }, strike: null };
  const settles = mark === "fp";
  return {
    level: settles ? { ticks, velocity: VELOCITIES.p } : null,
    strike: { ticks, velocity: VELOCITIES[mark], settles },
  };
}

/** A curve from marks collected in any order. */
export function curveOf(
  levels: readonly Level[],
  ramps: readonly Ramp[],
  strikes: readonly Strike[],
  base: number,
): Curve {
  return {
    levels: [...levels].sort((one, other) => one.ticks - other.ticks),
    ramps: ramps.filter((ramp) => ramp.to > ramp.from),
    strikes: new Map(strikes.map((strike) => [strike.ticks, strike])),
    base,
  };
}

/** What each hairpin reaches, worked out once: a chain of them asks for it again and again. */
const REACHED = new WeakMap<Ramp, number>();

/**
 * The step in force at a moment, ignoring any slope running through it: the last mark, or
 * where a hairpin that ended since took the music.
 */
function levelAt(curve: Curve, ticks: number): number {
  let velocity = curve.base;
  let since = -Infinity;
  for (const level of curve.levels) {
    if (level.ticks > ticks) break;
    velocity = level.velocity;
    since = level.ticks;
  }
  let ended: Ramp | undefined;
  for (const ramp of curve.ramps) {
    if (ramp.to <= ticks && ramp.to > since && (ended === undefined || ramp.to > ended.to)) {
      ended = ramp;
    }
  }
  return ended === undefined ? velocity : reachedBy(curve, ended);
}

/** Where a hairpin starts and every mark written under it, in order: what it slopes through. */
function passes(curve: Curve, ramp: Ramp): readonly Level[] {
  const inside = curve.levels.filter((level) => level.ticks > ramp.from && level.ticks < ramp.to);
  return [{ ticks: ramp.from, velocity: levelAt(curve, ramp.from) }, ...inside];
}

/** Where a hairpin takes the music: on from the last mark under it, or from where it began. */
function reachedBy(curve: Curve, ramp: Ramp): number {
  const known = REACHED.get(ramp);
  if (known !== undefined) return known;
  const points = passes(curve, ramp);
  const reached = target(curve, ramp, points[points.length - 1]!.velocity);
  REACHED.set(ramp, reached);
  return reached;
}

/**
 * What a hairpin is growing towards: the next mark if it reaches one, or a step of its own.
 *
 * A mark the hairpin would have to turn round to reach is not what it grows towards: a
 * diminuendo followed by a *subito forte* falls, and the forte then comes suddenly.
 */
function target(curve: Curve, ramp: Ramp, start: number): number {
  for (const level of curve.levels) {
    if (level.ticks < ramp.to) continue;
    if (level.ticks > ramp.to + REACH) break;
    if (ramp.rising ? level.velocity > start : level.velocity < start) return level.velocity;
    break;
  }
  return start * (ramp.rising ? GROWTH : DECAY);
}

/** A velocity kept inside what may be sounded. */
function bounded(velocity: number): number {
  return Math.min(CEILING, Math.max(FLOOR, velocity));
}

/**
 * How loud a note at this moment is.
 *
 * A note standing exactly where a hairpin closes belongs to what follows the hairpin, not
 * to the hairpin, which is why the slope is open at its far end: the mark written there is
 * what that note is worth.
 */
function velocityAt(curve: Curve, ticks: number): number {
  const level = levelAt(curve, ticks);
  const ramp = curve.ramps.find((span) => span.from <= ticks && ticks < span.to);
  if (ramp === undefined) return bounded(level);

  const points = [...passes(curve, ramp), { ticks: ramp.to, velocity: reachedBy(curve, ramp) }];
  // The points run from the ramp's start to its end and `ticks` is inside it, so there is one
  // at or before it and one after it.
  const after = points.findIndex((point) => point.ticks > ticks);
  const one = points[after - 1]!;
  const other = points[after]!;
  const through = (ticks - one.ticks) / (other.ticks - one.ticks);
  return bounded(one.velocity + (other.velocity - one.velocity) * through);
}

/**
 * How loud a note is struck, and how its loudness moves while it is held.
 *
 * `weight` is what the articulations on it multiply the dynamic by, and holds for the whole
 * note: an accented note in a crescendo grows as the crescendo does, from louder. `struck` is
 * where an *sf* or *fp* on the note stands: where its chord is written, which a rolled chord's
 * upper notes sound after.
 */
export function loudnessOf(
  curve: Curve,
  from: number,
  ticks: number,
  weight: number,
  struck: number,
): { readonly velocity: number; readonly swell: readonly Swell[] } {
  const strike = curve.strikes.get(struck);
  const velocity = bounded((strike?.velocity ?? velocityAt(curve, from)) * weight);
  const end = from + ticks;
  // A stressed note that does not settle is held at its stress, as an accented note is.
  if (ticks <= 1 || (strike !== undefined && !strike.settles)) return { velocity, swell: [] };

  const moving = curve.ramps.filter((ramp) => ramp.from < end && ramp.to > from);
  const settled = strike === undefined ? from : from + Math.min(SETTLE, Math.floor(ticks / 2));
  if (moving.length === 0 && settled === from) return { velocity, swell: [] };

  const at = new Set<number>([settled, end - 1]);
  for (const ramp of moving) {
    for (const edge of [ramp.from, ramp.to]) if (edge > settled && edge < end) at.add(edge);
    const first = Math.max(ramp.from, settled);
    for (
      let tick = first - (first % STRIDE) + STRIDE;
      tick < Math.min(ramp.to, end);
      tick += STRIDE
    ) {
      at.add(tick);
    }
  }
  const swell = [...at]
    .filter((tick) => tick > from && tick < end)
    .sort((one, other) => one - other)
    .map((tick) => ({
      at: (tick - from) / ticks,
      velocity: bounded(velocityAt(curve, tick) * weight),
    }));
  return swell.every((point) => point.velocity === velocity)
    ? { velocity, swell: [] }
    : { velocity, swell };
}
